import React, { useState } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput, TouchSelect, TouchFormGroup } from '../ui/TouchForm';

const TimeBasedFilter = ({ 
  timeRange, 
  onTimeRangeChange,
  customDateRange,
  onCustomDateRangeChange,
  lastUpdated,
  onLastUpdatedChange 
}) => {
  const [showCustomDates, setShowCustomDates] = useState(timeRange === 'custom');

  const timeRangeOptions = [
    { value: 'all', label: 'All Time', icon: '∞' },
    { value: 'last_day', label: 'Last 24 Hours', icon: '📅' },
    { value: 'last_week', label: 'Last Week', icon: '📅' },
    { value: 'last_month', label: 'Last Month', icon: '📅' },
    { value: 'last_3_months', label: 'Last 3 Months', icon: '📅' },
    { value: 'last_6_months', label: 'Last 6 Months', icon: '📅' },
    { value: 'last_year', label: 'Last Year', icon: '📅' },
    { value: 'custom', label: 'Custom Range', icon: '📊' }
  ];

  const lastUpdatedOptions = [
    { value: null, label: 'Any Update Time' },
    { value: 'last_hour', label: 'Updated in Last Hour' },
    { value: 'last_day', label: 'Updated Today' },
    { value: 'last_week', label: 'Updated This Week' },
    { value: 'last_month', label: 'Updated This Month' }
  ];

  const handleTimeRangeChange = (value) => {
    onTimeRangeChange(value);
    setShowCustomDates(value === 'custom');
    
    if (value !== 'custom') {
      onCustomDateRangeChange({ start: null, end: null });
    }
  };

  const handleCustomDateChange = (type, value) => {
    const newRange = {
      ...customDateRange,
      [type]: value
    };
    onCustomDateRangeChange(newRange);
  };

  const getPresetDates = (preset) => {
    const now = new Date();
    const start = new Date();
    
    switch (preset) {
      case 'last_week':
        start.setDate(now.getDate() - 7);
        break;
      case 'last_month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'last_3_months':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'last_6_months':
        start.setMonth(now.getMonth() - 6);
        break;
      case 'last_year':
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return null;
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: now.toISOString().split('T')[0]
    };
  };

  const applyPresetDates = (preset) => {
    const dates = getPresetDates(preset);
    if (dates) {
      onCustomDateRangeChange(dates);
      onTimeRangeChange('custom');
      setShowCustomDates(true);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Review Time Range */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          📅 Review Time Period:
        </label>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {timeRangeOptions.map(option => (
            <TouchButton
              key={option.value}
              onClick={() => handleTimeRangeChange(option.value)}
              variant={timeRange === option.value ? 'primary' : 'outline'}
              size="small"
              style={{ 
                fontSize: '12px',
                justifyContent: 'flex-start',
                gap: '6px'
              }}
            >
              <span>{option.icon}</span>
              <span>{option.label}</span>
            </TouchButton>
          ))}
        </div>
      </TouchFormGroup>

      {/* Custom Date Range */}
      {showCustomDates && (
        <TouchFormGroup>
          <label style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            marginBottom: '12px',
            display: 'block'
          }}>
            📊 Custom Date Range:
          </label>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                From Date
              </label>
              <TouchInput
                type="date"
                value={customDateRange.start || ''}
                onChange={(e) => handleCustomDateChange('start', e.target.value)}
              />
            </div>
            
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              alignSelf: 'flex-end',
              paddingBottom: '12px'
            }}>
              to
            </div>
            
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                To Date
              </label>
              <TouchInput
                type="date"
                value={customDateRange.end || ''}
                onChange={(e) => handleCustomDateChange('end', e.target.value)}
                min={customDateRange.start}
              />
            </div>
          </div>
          
          {/* Quick Date Presets */}
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
              Quick Presets:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {[
                { key: 'last_week', label: 'Last 7 Days' },
                { key: 'last_month', label: 'Last 30 Days' },
                { key: 'last_3_months', label: 'Last 3 Months' },
                { key: 'last_6_months', label: 'Last 6 Months' }
              ].map(preset => (
                <TouchButton
                  key={preset.key}
                  onClick={() => applyPresetDates(preset.key)}
                  variant="ghost"
                  size="small"
                  style={{ fontSize: '11px' }}
                >
                  {preset.label}
                </TouchButton>
              ))}
            </div>
          </div>
        </TouchFormGroup>
      )}

      {/* Last Updated Filter */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '8px',
          display: 'block'
        }}>
          🔄 Last Updated:
        </label>
        
        <TouchSelect
          value={lastUpdated || ''}
          onChange={(e) => onLastUpdatedChange(e.target.value || null)}
        >
          {lastUpdatedOptions.map(option => (
            <option key={option.value || 'null'} value={option.value || ''}>
              {option.label}
            </option>
          ))}
        </TouchSelect>
      </TouchFormGroup>

      {/* Time-based Quick Filters */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          ⚡ Quick Time Filters:
        </label>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <TouchButton
            onClick={() => handleTimeRangeChange('last_day')}
            variant={timeRange === 'last_day' ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            🔥 Recent (24h)
          </TouchButton>
          
          <TouchButton
            onClick={() => handleTimeRangeChange('last_week')}
            variant={timeRange === 'last_week' ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            📈 This Week
          </TouchButton>
          
          <TouchButton
            onClick={() => handleTimeRangeChange('last_month')}
            variant={timeRange === 'last_month' ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            📊 This Month
          </TouchButton>
          
          <TouchButton
            onClick={() => {
              onTimeRangeChange('all');
              onLastUpdatedChange('last_day');
            }}
            variant={timeRange === 'all' && lastUpdated === 'last_day' ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            🆕 Recently Updated
          </TouchButton>
        </div>
      </TouchFormGroup>

      {/* Active Filter Summary */}
      {(timeRange !== 'all' || lastUpdated) && (
        <div style={{
          padding: '12px',
          backgroundColor: '#e8f5e8',
          borderRadius: '8px',
          border: '1px solid #4CAF50'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#2E7D32', marginBottom: '6px' }}>
            Active Time Filters:
          </div>
          <div style={{ fontSize: '12px', color: '#333' }}>
            {timeRange !== 'all' && (
              <div>
                Time Range: {timeRangeOptions.find(o => o.value === timeRange)?.label || timeRange}
              </div>
            )}
            {timeRange === 'custom' && customDateRange.start && customDateRange.end && (
              <div>
                Custom: {customDateRange.start} to {customDateRange.end}
              </div>
            )}
            {lastUpdated && (
              <div>
                Updates: {lastUpdatedOptions.find(o => o.value === lastUpdated)?.label}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeBasedFilter;